import { useState } from "react";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useConvexAuth, useConvexProjects, useConvexInvestments } from "../services/convexService";
import { toast } from "sonner";

const SchemaTest = () => {
  const { registerInvestor, registerProjectHolder } = useConvexAuth();
  const { createProject } = useConvexProjects();
  const { createInvestment } = useConvexInvestments();
  const [testResults, setTestResults] = useState<string[]>([]);

  const addResult = (result: string) => {
    setTestResults(prev => [...prev, result]);
  };

  const testUserCreation = async () => {
    try {
      addResult("Testing user creation...");

      // Test investor registration
      const investorSuccess = await registerInvestor({
        name: "Test Investor",
        email: `investor-${Date.now()}@test.com`,
        password: "test123",
        phone: "+1234567890",
        company: "Test Investment Corp",
        bio: "Professional investor",
        location: "Test City"
      });

      if (investorSuccess) {
        addResult("✓ Investor registration successful");
      } else {
        addResult("✗ Investor registration failed");
      }

      // Test project holder registration
      const projectHolderSuccess = await registerProjectHolder({
        name: "Test Project Holder",
        email: `holder-${Date.now()}@test.com`,
        password: "test123",
        phone: "+1234567890",
        company: "Test Startup Inc",
        bio: "Entrepreneur with innovative ideas",
        location: "Test City"
      });

      if (projectHolderSuccess) {
        addResult("✓ Project holder registration successful");
      } else {
        addResult("✗ Project holder registration failed");
      }
    } catch (error: any) {
      addResult(`✗ User creation error: ${error.message}`);
    }
  };

  const testProjectCreation = async () => {
    try {
      addResult("Testing project creation...");
      // In a real test, we would create a project here
      addResult("✓ Project creation test completed (mock)");
    } catch (error: any) {
      addResult(`✗ Project creation error: ${error.message}`);
    }
  };

  const testInvestmentCreation = async () => {
    try {
      addResult("Testing investment creation...");
      // In a real test, we would create an investment here
      addResult("✓ Investment creation test completed (mock)");
    } catch (error: any) {
      addResult(`✗ Investment creation error: ${error.message}`);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Database Schema Test</h1>
            <p className="text-muted-foreground">
              Testing the enhanced database schema functionality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle>User Creation</CardTitle>
              </CardHeader>
              <CardContent>
                <Button onClick={testUserCreation} className="w-full">
                  Test User Creation
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Project Creation</CardTitle>
              </CardHeader>
              <CardContent>
                <Button onClick={testProjectCreation} className="w-full">
                  Test Project Creation
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Investment Creation</CardTitle>
              </CardHeader>
              <CardContent>
                <Button onClick={testInvestmentCreation} className="w-full">
                  Test Investment Creation
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Test Results</CardTitle>
                <Button variant="outline" onClick={clearResults}>
                  Clear Results
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {testResults.length > 0 ? (
                <div className="space-y-2">
                  {testResults.map((result, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded ${result.startsWith('✓') ? 'bg-green-100 text-green-800' : result.startsWith('✗') ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {result}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Click the buttons above to run tests. Results will appear here.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SchemaTest;